import { getFlowDataDirect } from "@adaptis/flow-components";
import { getPetQty } from "./data-store";

declare global {
  interface Window {
    /** Hàng đợi Klaviyo onsite — https://developers.klaviyo.com/en/docs/javascript_api */
    _learnq?: unknown[];
    sessionRewind?: {
      identifyUser: (opts: {
        userId: string;
        email: string;
        phone: string;
        firstName: string;
        lastName: string;
      }) => void;
      setSessionInfo?: (info: SessionRewindSessionInfo) => void;
      /**
       * Callback: dùng URL bên trong callback khi recording sẵn sàng.
       * Đồng bộ không tham số: có thể null nếu SDK chưa khởi tạo (Session Rewind / Sentry docs).
       */
      getSessionUrl?: {
        (callback: (sessionUrl: string) => void): void;
        (): string | null | undefined;
      };
    };
  }
}

/** Metadata quote sau step-1 — toàn bộ giá trị string (theo Session Rewind). */
export type SessionRewindSessionInfo = {
  sessionId: string;
  suburb: string;
  postCode: string;
  state: string;
  numberOfDogs: string;
  numberOfCats: string;
  channel: string;
  landingPage: string;
  costcoMember: string;
};

const RETRY_MS = 250;
const MAX_RETRIES = 80;

let lastIdentifiedKey = "";
let lastAttemptKey = "";
let lastSessionInfoKey = "";
let lastKlaviyoSessionRewindPushKey = "";
let retryCount = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

function buildIdentifyKey(
  email: string,
  phone: string,
  firstName: string,
  lastName: string
): string {
  return `${email}|${phone}|${firstName}|${lastName}`;
}

function clearRetry(): void {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function isTruthyFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes";
  }
  return false;
}

function buildSessionRewindSessionInfo(): SessionRewindSessionInfo {
  const pets = getFlowDataDirect("pets");
  const { dogQty, catQty } = getPetQty(pets);
  const channelRaw =
    getFlowDataDirect("channel") ?? getFlowDataDirect("leadSource") ?? "PetsyQT";
  const costcoRaw = getFlowDataDirect("costcoMember");
  return {
    sessionId: String(getFlowDataDirect("sessionId") ?? ""),
    suburb: String(getFlowDataDirect("holderAddress.suburb") ?? ""),
    postCode: String(getFlowDataDirect("holderAddress.postcode") ?? ""),
    state: String(getFlowDataDirect("holderAddress.state") ?? ""),
    numberOfDogs: String(dogQty),
    numberOfCats: String(catQty),
    channel: String(channelRaw),
    landingPage: String(getFlowDataDirect("landingPage") ?? ""),
    costcoMember: isTruthyFlag(costcoRaw) ? "true" : "false",
  };
}

function pushSessionRewindUrlToKlaviyo(email: string, sessionUrl: string): void {
  if (!email || !sessionUrl) {
    return;
  }
  const learnq = window._learnq ?? [];
  window._learnq = learnq;
  learnq.push([
    "identify",
    {
      $email: email,
      SessionRewindUrl: sessionUrl,
    },
  ]);
  learnq.push([
    "track",
    "Session Rewind Captured",
    {
      replayUrl: sessionUrl,
    },
  ]);
}

/**
 * getSessionUrl là async — Klaviyo chỉ được cập nhật trong callback.
 */
function requestSessionRewindUrlForKlaviyo(email: string): void {
  const trimmed = email.trim();
  if (!trimmed) {
    return;
  }
  const getSessionUrl = window.sessionRewind?.getSessionUrl;
  if (typeof getSessionUrl !== "function") {
    return;
  }
  getSessionUrl((sessionUrl: string) => {
    if (!sessionUrl || typeof sessionUrl !== "string") {
      return;
    }
    const pushKey = `${trimmed}|${sessionUrl}`;
    if (pushKey === lastKlaviyoSessionRewindPushKey) {
      return;
    }
    lastKlaviyoSessionRewindPushKey = pushKey;
    pushSessionRewindUrlToKlaviyo(trimmed, sessionUrl);
  });
}

/**
 * Sau submit step-1: gửi identify (khi có email) + setSessionInfo (quote context).
 * Retry ngắn nếu srloader.js chưa gắn window.sessionRewind.
 */
export function applySessionRewindAfterStep1Submit(): void {
  const emailRaw = (getFlowDataDirect("holderEmail") ?? "").trim();
  const phone = String(getFlowDataDirect("holderPhone") ?? "").trim();
  const firstName = String(getFlowDataDirect("holderFirstName") ?? "").trim();
  const lastName = String(getFlowDataDirect("holderLastName") ?? "").trim();

  if (emailRaw) {
    const ik = buildIdentifyKey(emailRaw, phone, firstName, lastName);
    if (ik !== lastAttemptKey) {
      lastAttemptKey = ik;
      retryCount = 0;
      clearRetry();
    }
  }

  const sr = window.sessionRewind;
  if (!sr?.identifyUser) {
    if (retryCount >= MAX_RETRIES) {
      return;
    }
    if (!retryTimer) {
      retryTimer = setTimeout(() => {
        retryTimer = null;
        retryCount += 1;
        applySessionRewindAfterStep1Submit();
      }, RETRY_MS);
    }
    return;
  }

  clearRetry();
  retryCount = 0;

  if (emailRaw) {
    const key = buildIdentifyKey(emailRaw, phone, firstName, lastName);
    if (key !== lastIdentifiedKey) {
      lastIdentifiedKey = key;
      sr.identifyUser({
        userId: emailRaw,
        email: emailRaw,
        phone,
        firstName,
        lastName: lastName || "",
      });
    }
  }

  if (typeof sr.setSessionInfo === "function") {
    const info = buildSessionRewindSessionInfo();
    const sessionKey = JSON.stringify(info);
    if (sessionKey !== lastSessionInfoKey) {
      lastSessionInfoKey = sessionKey;
      sr.setSessionInfo(info);
    }
  }

  if (emailRaw) {
    requestSessionRewindUrlForKlaviyo(emailRaw);
  }
}